/**
 * Cliente fire-and-forget pra POST /api/v1/qr-events.
 *
 * Usa `navigator.sendBeacon` quando disponível — garante envio mesmo se
 * a navegação interromper o request (ex.: clique em link externo pra
 * Play Store). Fallback `fetch({ keepalive: true })` cobre browsers que
 * não suportam ou recusam sendBeacon (ex.: alguns webviews in-app).
 *
 * NUNCA bloqueia UX — falha silenciosamente. O backend é idempotente,
 * então retry é seguro mas evitamos pra não duplicar.
 */

import { useAuthStore } from '@/store/auth';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export type QrEventType =
  | 'view'
  | 'landing_loaded'
  | 'available_seen'
  | 'connector_selected'
  | 'cta_click'
  | 'cta_click_logged_in'
  | 'auth_completed'
  | 'other_app_click'
  | 'landing_left'
  | 'error';

export interface QrEventContext {
  external_app_id?: string;
  error_code?: string;
  time_on_page_ms?: number;
  connectors_available_count?: number;
  connectors_total_count?: number;
  /** Conector escolhido no Passo 1 da landing (connector_number). */
  connector_number?: number;
  latency_render_ms?: number;
  /** Destino real do CTA da landing (funil pós-mudança de 2026-07, quando o
   *  botão da Bow passou a empurrar o app nativo). 'scheme_ios' reservado
   *  pro flip do iOS pós-adoção da release 1.2. */
  cta_target?:
    | 'native_in_app'
    | 'intent_android'
    | 'store_play'
    | 'store_apple'
    | 'pwa_desktop'
    | 'pwa_fallback'
    | 'scheme_ios';
}

export interface EmitParams {
  qrSessionId: string;
  hubStationId: string;
  qrVersion?: string | null;
  eventType: QrEventType;
  context?: QrEventContext;
}

export function emitQrEvent(params: EmitParams): void {
  const body = JSON.stringify({
    qr_session_id: params.qrSessionId,
    hub_station_id: params.hubStationId,
    qr_version: params.qrVersion ?? null,
    event_type: params.eventType,
    context: params.context ?? {}
  });
  const url = `${API_BASE}/qr-events`;

  // sendBeacon é o caminho preferido — sobrevive a navegação/unload.
  // Aceita Blob, mas o Content-Type vai como text/plain; backend Pydantic
  // ignora o header e aceita JSON no body. Verificamos pegando JSON manual.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    } catch {
      /* fallthrough pra fetch */
    }
  }

  // Fallback fetch keepalive (sobrevive a unload em browsers que suportam).
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  void fetch(url, { method: 'POST', body, headers, keepalive: true }).catch(() => {
    /* ignore — métrica não bloqueia UX */
  });
}
